import { apiClient } from './client';
import { SourceEpisode, SourcePanel } from '../../types';

export interface CreateSourceEpisodeRequest {
  name: string;
  orderIndex?: number;
  startPanelId?: string;
  endPanelId?: string;
  thumbnailUrl?: string;
  status?: 'DRAFT' | 'PUBLISHED';
}

export interface UpdateSourceEpisodeRequest extends Partial<CreateSourceEpisodeRequest> {
  speedMultiplier?: number;
}

export const sourceEpisodesApi = {
  getEpisodes: async (storyId: string): Promise<{ data: SourceEpisode[] }> => {
    return apiClient.get<{ data: SourceEpisode[] }>(`/api/v1/admin/source-stories/${storyId}/episodes`);
  },

  getEpisodeDetail: async (id: string): Promise<{ data: SourceEpisode }> => {
    return apiClient.get<{ data: SourceEpisode }>(`/api/v1/admin/source-episodes/${id}`);
  },

  createEpisode: async (storyId: string, data: CreateSourceEpisodeRequest): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(`/api/v1/admin/source-stories/${storyId}/episodes`, data);
  },

  updateEpisode: async (id: string, data: UpdateSourceEpisodeRequest): Promise<{ data: SourceEpisode }> => {
    return apiClient.patch<{ data: SourceEpisode }>(`/api/v1/admin/source-episodes/${id}`, data);
  },

  deleteEpisode: async (id: string): Promise<{ data: { success: boolean } }> => {
    return apiClient.delete<{ data: { success: boolean } }>(`/api/v1/admin/source-episodes/${id}`);
  },

  getEpisodePanels: async (id: string): Promise<{ data: SourcePanel[] }> => {
    return apiClient.get<{ data: SourcePanel[] }>(`/api/v1/admin/source-episodes/${id}/panels`);
  },
  
  exportEpisodeMaterials: async (episodeId: string): Promise<Blob> => {
    return apiClient.getBinary(`/api/v1/admin/source-episodes/${episodeId}/export`);
  },

  generateMasterAudio: async (storyId: string, id: string): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(`/api/v1/admin/source-stories/${storyId}/episodes/${id}/generate-master-audio`);
  },

  analyzeTimestamps: async (storyId: string, id: string): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(`/api/v1/admin/source-stories/${storyId}/episodes/${id}/analyze-timestamps`);
  },

  processAudio: async (storyId: string, id: string, chunkSize: number): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(`/api/v1/admin/source-stories/${storyId}/episodes/${id}/process-audio`, { chunkSize });
  },

  analyzeMotion: async (storyId: string, id: string): Promise<{ data: SourceEpisode; averageConfidence?: number; totalEffects?: number; effectsDistribution?: Record<string, number> }> => {
    return apiClient.post<{ data: SourceEpisode; averageConfidence?: number; totalEffects?: number; effectsDistribution?: Record<string, number> }>(`/api/v1/admin/source-stories/${storyId}/episodes/${id}/analyze-motion`);
  },

  // V2: Add motion effect to panel
  addMotionEffect: async (
    episodeId: string,
    panelId: string,
    effect: {
      type: string;
      startOffset: number;
      duration: number;
      intensity: number;
      focusPoint?: { x: number; y: number };
      overlay?: boolean;
    }
  ): Promise<{ success: boolean; effect: any }> => {
    return apiClient.post<{ success: boolean; effect: any }>(
      `/api/v1/admin/source-episodes/${episodeId}/panels/${panelId}/motion-effects`,
      effect
    );
  },

  // V2: Update motion effect
  updateMotionEffect: async (
    episodeId: string,
    panelId: string,
    effectId: string,
    updates: {
      type?: string;
      startOffset?: number;
      duration?: number;
      intensity?: number;
      focusPoint?: { x: number; y: number };
      overlay?: boolean;
    }
  ): Promise<{ success: boolean; effect: any }> => {
    return apiClient.patch<{ success: boolean; effect: any }>(
      `/api/v1/admin/source-episodes/${episodeId}/panels/${panelId}/motion-effects/${effectId}`,
      updates
    );
  },

  // V2: Delete motion effect
  deleteMotionEffect: async (
    episodeId: string,
    panelId: string,
    effectId: string
  ): Promise<{ success: boolean }> => {
    return apiClient.delete<{ success: boolean }>(
      `/api/v1/admin/source-episodes/${episodeId}/panels/${panelId}/motion-effects/${effectId}`
    );
  },

  // Epic 3 V2: Generate video (async - returns jobId)
  generateVideo: async (episodeId: string, panelSyncData?: any[]): Promise<{
    data: {
      success: boolean;
      jobId?: string;
      error?: string;
    }
  }> => {
    return apiClient.post<{ data: { success: boolean; jobId?: string; error?: string } }>(
      `/api/v1/admin/source-episodes/${episodeId}/generate-video`,
      { panelSyncData }
    );
  },

  // Get video render job status
  getVideoRenderJobStatus: async (jobId: string): Promise<{
    data: {
      jobId: string;
      episodeId: string;
      status: string;
      progress: number;
      errorMessage?: string;
      videoUrl?: string;
    }
  }> => {
    return apiClient.get<{
      data: {
        jobId: string;
        episodeId: string;
        status: string;
        progress: number;
        errorMessage?: string;
        videoUrl?: string;
      }
    }>(
      `/api/v1/admin/video-render-jobs/${jobId}`
    );
  },

  // Epic 3 V2: Get video status
  getVideoStatus: async (episodeId: string): Promise<{
    data: {
      status: string;
      videoUrl?: string;
      error?: string;
      metadata?: any;
    };
  }> => {
    return apiClient.get<{
      data: {
        status: string;
        videoUrl?: string;
        error?: string;
        metadata?: any;
      };
    }>(`/api/v1/admin/source-episodes/${episodeId}/video-status`);
  },

  // Epic 3 V2: Delete video
  deleteVideo: async (episodeId: string): Promise<{ success: boolean }> => {
    return apiClient.delete<{ success: boolean }>(
      `/api/v1/admin/source-episodes/${episodeId}/video-output`
    );
  },

  // Epic 5: Complete analysis (Motion -> Music -> SFX sequentially)
  analyzeComplete: async (storyId: string, episodeId: string, enableBgm?: boolean, enableSfx?: boolean): Promise<{ data: any }> => {
    return apiClient.post<{ data: any }>(
      `/api/v1/admin/source-stories/${storyId}/episodes/${episodeId}/analyze-complete`,
      { enableBgm, enableSfx }
    );
  },

  // Epic 5: Audio-only analysis (Music -> SFX)
  analyzeAudioOnly: async (storyId: string, episodeId: string, enableBgm?: boolean, enableSfx?: boolean): Promise<{ data: any }> => {
    return apiClient.post<{ data: any }>(
      `/api/v1/admin/source-stories/${storyId}/episodes/${episodeId}/analyze-audio-only`,
      { enableBgm, enableSfx }
    );
  },

  analyzeMusic: async (storyId: string, id: string): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(`/api/v1/admin/source-stories/${storyId}/episodes/${id}/analyze-music`);
  },

  analyzeSfx: async (storyId: string, id: string): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(`/api/v1/admin/source-stories/${storyId}/episodes/${id}/analyze-sfx`);
  },

  // Epic 5: Update background music
  updateBackgroundMusic: async (
    episodeId: string, 
    backgroundMusicId: string | null, 
    backgroundVolume: number
  ): Promise<{ data: { success: boolean } }> => {
    return apiClient.patch<{ data: { success: boolean } }>(
      `/api/v1/admin/source-episodes/${episodeId}/background-music`,
      { backgroundMusicId, backgroundVolume }
    );
  },

  // Epic 5: Add sound effect to panel
  addPanelSoundEffect: async (
    episodeId: string,
    panelId: string,
    soundEffect: {
      audioId: string;
      triggerTime: number;
      volume: number;
      reasoning?: string;
    }
  ): Promise<{ data: { success: boolean; soundEffect: any } }> => {
    return apiClient.post<{ data: { success: boolean; soundEffect: any } }>(
      `/api/v1/admin/source-episodes/${episodeId}/panels/${panelId}/sound-effects`,
      soundEffect
    );
  },

  // Epic 5: Update sound effect in panel
  updatePanelSoundEffect: async (
    episodeId: string,
    panelId: string,
    soundEffectId: string,
    updates: {
      audioId?: string;
      triggerTime?: number;
      volume?: number;
      reasoning?: string;
    }
  ): Promise<{ data: { success: boolean; soundEffect: any } }> => {
    return apiClient.patch<{ data: { success: boolean; soundEffect: any } }>(
      `/api/v1/admin/source-episodes/${episodeId}/panels/${panelId}/sound-effects/${soundEffectId}`,
      updates
    );
  },

  // Epic 5: Delete sound effect from panel
  deletePanelSoundEffect: async (
    episodeId: string,
    panelId: string,
    soundEffectId: string
  ): Promise<{ data: { success: boolean } }> => {
    return apiClient.delete<{ data: { success: boolean } }>(
      `/api/v1/admin/source-episodes/${episodeId}/panels/${panelId}/sound-effects/${soundEffectId}`
    );
  },

  // Epic 5: Batch update all panels data
  updatePanelsData: async (
    episodeId: string,
    panels: any[]
  ): Promise<{ data: { success: boolean } }> => {
    return apiClient.patch<{ data: { success: boolean } }>(
      `/api/v1/admin/source-episodes/${episodeId}/panels`,
      { panels }
    );
  },

  // 🆕 Epic 6: Regenerate subtitle VTT files from subtitleEntries
  regenerateSubtitles: async (episodeId: string): Promise<{ data: { success: boolean } }> => {
    return apiClient.post<{ data: { success: boolean } }>(
      `/api/v1/admin/source-episodes/${episodeId}/subtitles/regenerate`
    );
  },

  // Generate hook concept
  generateHookConcept: async (
    episodeId: string,
    params: { hookPassScore?: number; maxHookRetries?: number } = {}
  ): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(
      `/api/v1/admin/source-episodes/${episodeId}/hooks/generate`,
      params
    );
  },

  // Select hook panel images
  selectHookPanelImages: async (episodeId: string, hookId: string): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(
      `/api/v1/admin/source-episodes/${episodeId}/hooks/${hookId}/select-images`
    );
  },

  // Apply a hook to an episode
  applyHook: async (episodeId: string, hookId?: string): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(
      `/api/v1/admin/source-episodes/${episodeId}/hooks/apply`,
      { hookId }
    );
  },

  // Create a manual hook
  createHook: async (
    episodeId: string,
    data: { concept: string; panels: Array<{ caption: string }> }
  ): Promise<{ data: SourceEpisode }> => {
    return apiClient.post<{ data: SourceEpisode }>(
      `/api/v1/admin/source-episodes/${episodeId}/hooks`,
      data
    );
  },

  // Update a hook
  updateHook: async (
    episodeId: string,
    hookId: string,
    data: { concept: string; panels: Array<{ id?: string; caption: string }> }
  ): Promise<{ data: SourceEpisode }> => {
    return apiClient.put<{ data: SourceEpisode }>(
      `/api/v1/admin/source-episodes/${episodeId}/hooks/${hookId}`,
      data
    );
  },

  // Set hook panel image
  setHookPanelImage: async (
    episodeId: string,
    hookId: string,
    panelId: string,
    imageUrl: string
  ): Promise<{ data: SourceEpisode }> => {
    return apiClient.put<{ data: SourceEpisode }>(
      `/api/v1/admin/source-episodes/${episodeId}/hooks/${hookId}/panels/${panelId}/image`,
      { imageUrl }
    );
  },

  // Upload hook panel image
  uploadHookPanelImage: async (
    episodeId: string,
    hookId: string,
    panelId: string,
    file: File
  ): Promise<{ data: SourceEpisode }> => {
    const formData = new FormData();
    formData.append('image', file);

    return apiClient.post<{ data: SourceEpisode }>(
      `/api/v1/admin/source-episodes/${episodeId}/hooks/${hookId}/panels/${panelId}/image/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },
};
